import { AUTH_ACTION_TYPE } from 'enums';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { t } from 'services/i18n.service';
import { AuthPageContext } from '../AuthActionsWrapper';
import {
  AUTH_SCREEN,
  EMPTY_STRING,
  LOCAL_STORAGE
} from 'constants/general';
import { PARAMS } from 'constants/params';
import GoogleLogo from 'assets/icons/GoogleLogo';
import {
  getAuth0User,
  isDesktopLoginRequest,
  loginWithGoogle
} from 'services/auth.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { onboardFlowSignInSelector } from 'store/reducer/onboard-flow/selectors';
import { toast } from 'react-toastify';
import { updateIsNewUserSigninProcess } from 'store/reducer/setting/slice';
import { ROUTES } from 'constants/routes';
import BaseButton from 'components/ui/BaseButton';
import { removeLocalStorage } from 'utils/helpers/storage';
import {
  captureException,
  capturePostHogEvent
} from 'services/posthog.service';
import { useNavigate } from 'react-router-dom';
import { POST_HOG_EVENT } from 'constants/events';
import { isMobileDevice } from 'utils/device.util';
import { isSupportPanelPage } from 'utils/support-panel.util';

const GoogleAuth = () => {
  const {
    state: {
      isProcessing,
      googleLoginInProgress,
      appleLoginInProgress,
      screen
    },
    dispatch
  } = useContext(AuthPageContext);

  const appDispatch = useAppDispatch();
  const isNewUserSigninProcess = useAppSelectorShallow(
    onboardFlowSignInSelector
  );
  const navigate = useNavigate();

  const clickLockRef = useRef(false);
  const [isStartingGoogleLogin, setIsStartingGoogleLogin] = useState(false);

  const isGoogleBusy = googleLoginInProgress || isStartingGoogleLogin;

  const isAnyAuthActionInProgress = useMemo(
    () =>
      isProcessing ||
      googleLoginInProgress ||
      appleLoginInProgress ||
      isNewUserSigninProcess ||
      isStartingGoogleLogin,
    [
      isProcessing,
      googleLoginInProgress,
      appleLoginInProgress,
      isNewUserSigninProcess,
      isStartingGoogleLogin
    ]
  );

  const resetGoogleState = useCallback(() => {
    clickLockRef.current = false;
    setIsStartingGoogleLogin(false);

    dispatch?.({
      type: AUTH_ACTION_TYPE.UPDATE_GOOGLE_LOGIN_IN_PROGRESS,
      value: false
    });
    dispatch?.({
      type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING,
      value: false
    });
  }, [dispatch]);

  const handleLoginCallback = useCallback(
    async (hasError: boolean, error?: string) => {
      if (hasError) {
        if (error) {
          toast.error(error);
        }

        resetGoogleState();

        captureException(error);
        capturePostHogEvent(POST_HOG_EVENT.GOOGLE_LOGIN.ERROR, {
          message: error
        });
        return;
      }

      if (isSupportPanelPage()) {
        // Agents sign in to the Zoho-framed panel, not up: skip onboarding and stay on its path.
        capturePostHogEvent(POST_HOG_EVENT.SIGN_IN.GOOGLE_SIGN_IN_SUCCESS);
        navigate(ROUTES.SUPPORT_PANEL, { replace: true });
        return;
      }

      let nextRoute = ROUTES.HOME;
      const auth0GoogleUser = await getAuth0User();
      const isFirstLoggedIn =
        auth0GoogleUser?.['https://dashboard.focusbear.io/first_login'] ??
        false;

      let eventName = POST_HOG_EVENT.SIGN_IN.GOOGLE_SIGN_IN_SUCCESS;

      if (isFirstLoggedIn) {
        appDispatch(updateIsNewUserSigninProcess(true));
        eventName = isDesktopLoginRequest
          ? POST_HOG_EVENT.SIGN_UP.REDIRECT_TO_DESKTOP_SUCCESS
          : POST_HOG_EVENT.SIGN_UP.GOOGLE_SIGN_UP_SUCCESS;

        const nextNonMobileRoute = isDesktopLoginRequest
          ? `${ROUTES.DESKTOP_REDIRECT}?${PARAMS.SIGN_INTO_DESKTOP}=true`
          : ROUTES.DOWNLOAD_APPS;

        nextRoute = isMobileDevice()
          ? ROUTES.MOBILE_APP_DOWNLOAD
          : nextNonMobileRoute;
      } else {
        eventName = isDesktopLoginRequest
          ? POST_HOG_EVENT.SIGN_IN.REDIRECT_TO_DESKTOP_SUCCESS
          : eventName;

        nextRoute = isDesktopLoginRequest
          ? `${ROUTES.DESKTOP_REDIRECT}?${PARAMS.SIGN_INTO_DESKTOP}=true`
          : nextRoute;

        if (isDesktopLoginRequest) {
          removeLocalStorage([LOCAL_STORAGE.AUTH_BEARER_TOKEN]);
        }
      }

      capturePostHogEvent(eventName);
      navigate(nextRoute, { replace: true });
    },
    [appDispatch, navigate, resetGoogleState]
  );

  const handleContinueWithGoogle = useCallback(() => {
    if (clickLockRef.current) return;
    if (isAnyAuthActionInProgress) return;

    clickLockRef.current = true;
    setIsStartingGoogleLogin(true);

    dispatch?.({
      type: AUTH_ACTION_TYPE.UPDATE_GOOGLE_LOGIN_IN_PROGRESS,
      value: true
    });

    capturePostHogEvent(POST_HOG_EVENT.GOOGLE_LOGIN.CLICKED, {
      screen: screen === AUTH_SCREEN.SIGN_IN ? 'sign-in' : 'sign-up'
    });

    loginWithGoogle((hasError, error) => {
      handleLoginCallback(hasError, error);
    });
  }, [dispatch, handleLoginCallback, isAnyAuthActionInProgress, screen]);

  return (
    <BaseButton
      title={
        isGoogleBusy
          ? EMPTY_STRING
          : t(
              `auth_page.${
                screen === AUTH_SCREEN.SIGN_IN
                  ? 'continue_with_google'
                  : 'sign_up_with_google'
              }`
            )
      }
      icon={
        isGoogleBusy ? undefined : <GoogleLogo styles='~w-3/5 h-auto mr-1' />
      }
      additionalStyles='~min-w-44/96 w-fit ~px-5/10 py-1/2 self-center hover-scale'
      variant='secondary'
      disabled={isAnyAuthActionInProgress || clickLockRef.current}
      onClick={handleContinueWithGoogle}
      isLoading={isGoogleBusy}
    />
  );
};

export default GoogleAuth;
